import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ArrowUp, Check, Phone, MessageCircle, Users, Sprout, PenLine, Activity, NotebookPen, BookLock, Sparkles, LifeBuoy, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { BackButton, Spinner } from "@/components/Common";
import { COUNTRIES, Country, getCountry, setCountry, tel } from "@/lib/helplines";
import { TrustedContacts } from "@/components/TrustedContacts";

export const TZ = new Date().getTimezoneOffset();

export function HelpBox({ compact = false, country }: { compact?: boolean; country?: Country }) {
  const c = country || getCountry();
  const line = (num: string, name: string, note: string, id: string) => (
    <a key={id} href={tel(num)} className="row rounded-2xl" data-testid={`help-${id}`}>
      <Phone size={17} className="text-[hsl(var(--primary))] shrink-0" />
      <span className="flex-1 min-w-0"><span className="font-semibold">{name}</span> <span className="text-ink/62 text-sm">· {note}</span></span>
      <span className="font-mono text-sm font-bold shrink-0">{num}</span>
    </a>
  );
  return (
    <section className="surface p-2" data-testid="help-box">
      <div className="px-3 pt-3 pb-2">
        <h2 className="font-semibold">Hai bisogno di aiuto adesso? <span className="text-ink/55 font-normal text-sm" data-testid="help-country-name">· {c.name}</span></h2>
        {!compact && <p className="text-sm text-ink/66 mt-1">Se c'è un pericolo, dolore al petto, fatica a respirare, svenimento o pensieri di farti del male, chiama subito il {c.emergency}. UNSEEN non sostituisce medici né servizi di emergenza.</p>}
      </div>
      {line(c.emergency, "Emergenza", c.emergency === "112" ? "polizia, ambulanza, vigili del fuoco" : "emergenza nazionale", "emergency")}
      {c.emergency !== "112" && line("112", "Emergenza europea", "funziona anche qui", "112")}
      {(compact ? c.lines.slice(0, 2) : c.lines).map((l, i) => line(l.num, l.name, l.note, `line-${i}`))}
    </section>
  );
}

function TodayGoals() {
  const qc = useQueryClient();
  const { data: goals } = useQuery({ queryKey: ["goals", false], queryFn: () => api.get("/goals", { params: { tz: TZ, archived: false } }).then((r) => r.data) });
  const { data: score } = useQuery({ queryKey: ["score"], queryFn: () => api.get("/score", { params: { tz: TZ } }).then((r) => r.data) });
  const toggle = async (g: any) => {
    try {
      await (g.done_current ? api.delete(`/goals/${g.id}/complete`, { params: { tz: TZ } }) : api.post(`/goals/${g.id}/complete`, null, { params: { tz: TZ } }));
      ["goals", "score", "goal-history"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const list = (goals || []).filter((g: any) => g.frequency === "daily").slice(0, 4);
  return (
    <section className="surface p-2" data-testid="today-goals">
      <div className="flex items-center justify-between px-3 pt-3 pb-1">
        <h2 className="font-semibold">I tuoi obiettivi di oggi</h2>
        {score && <span className="text-sm text-ink/66" data-testid="today-progress">Completati oggi: {score.today}</span>}
      </div>
      {!goals ? <div className="p-4"><Spinner /></div> : list.length === 0 ? (
        <Link to="/cresci" className="row rounded-2xl text-ink/70" data-testid="today-goals-empty"><Sprout size={18} />Nessun obiettivo giornaliero. Creane uno piccolo in Cresci.<ArrowRight size={16} className="ml-auto" /></Link>
      ) : list.map((g: any) => (
        <button key={g.id} onClick={() => toggle(g)} className="row rounded-2xl" data-testid="today-goal-item" aria-pressed={g.done_current}>
          <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${g.done_current ? "bg-[hsl(var(--primary))] border-transparent text-white" : "border-ink/30 bg-white/50"}`}>{g.done_current && <Check size={14} strokeWidth={3} />}</span>
          <span className={`flex-1 ${g.done_current ? "line-through text-ink/55" : ""}`}>{g.title}</span>
        </button>
      ))}
      {list.length > 0 && <Link to="/cresci" className="row rounded-2xl text-sm text-ink/70" data-testid="today-goals-all">Tutti gli obiettivi<ArrowRight size={15} className="ml-auto" /></Link>}
    </section>
  );
}

function QuickSupport() {
  const item = (to: string, Icon: any, label: string, id: string) => (
    <Link to={to} className="surface p-4 flex flex-col gap-3 hover:bg-white/60" data-testid={`quick-${id}`}><Icon size={19} className="text-[hsl(var(--primary))]" /><span className="text-sm font-semibold leading-tight">{label}</span></Link>
  );
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="quick-support">
      {item("/ascolto", HeartHandshake, "Parla con qualcuno", "ascolto")}
      {item("/cresciamo", Users, "Cresciamo insieme", "cresciamo")}
      {item("/pubblica", PenLine, "Racconta la tua giornata", "pubblica")}
    </div>
  );
}

const CHIPS = [["bene", "Sto bene"], ["ansia", "Sono in ansia"], ["giu", "Mi sento giù"], ["solo", "Mi sento solo"]] as const;

function FeelComposer({ onSend, mood, setMood, compact = false }: any) {
  const [text, setText] = useState("");
  const send = () => { const t = text.trim(); if (!t && !mood) return; onSend(t || CHIPS.find(([k]) => k === mood)![1]); setText(""); };
  return (
    <div className="space-y-3">
      <div className="composer flex items-end gap-2 p-2" data-testid="feel-composer">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={compact ? 1 : 2} maxLength={4000} placeholder="Raccontami come ti senti…" data-testid="feel-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none bg-transparent outline-none px-3 py-2.5 text-[16px] leading-relaxed placeholder:text-ink/55 max-h-48" />
        <button onClick={send} disabled={!text.trim() && !mood} aria-label="Invia" className="send-btn" data-testid="feel-send-btn"><ArrowUp size={20} strokeWidth={2.4} /></button>
      </div>
      {!compact && (
        <div className="flex flex-wrap gap-2 justify-center" data-testid="mood-chips">
          {CHIPS.map(([k, l]) => <button key={k} onClick={() => setMood(mood === k ? null : k)} aria-pressed={mood === k} data-testid={`mood-${k}`} className={`chip min-h-[40px] px-4 text-sm ${mood === k ? "chip-violet" : "hover:bg-white/70"}`}>{l}</button>)}
        </div>
      )}
    </div>
  );
}

function Reflection({ lines, mood, onMore, onClose }: any) {
  const qc = useQueryClient();
  const [savedCount, setSavedCount] = useState(0);
  const saved = savedCount === lines.length;
  const save = async () => {
    try {
      await api.post("/journal", { tz: TZ, kind: "note", text: lines.slice(savedCount).join("\n\n"), mood }); // later additions are saved as a follow-up note
      setSavedCount(lines.length); ["journal", "score"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); toast.success("Salvato nel tuo diario privato");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const opt = (to: string, Icon: any, label: string, id: string) => <Link to={to} className="row rounded-2xl" data-testid={`reflect-${id}`}><Icon size={18} className="text-[hsl(var(--primary))]" /><span className="flex-1">{label}</span><ArrowRight size={16} className="text-ink/45" /></Link>;
  return (
    <div className="space-y-5" data-testid="reflection">
      <div className="space-y-2">{lines.map((l: string, i: number) => <div key={`${i}-${l.slice(0, 12)}`} className="flex justify-end"><p className="bubble-mine max-w-[85%] whitespace-pre-wrap break-words" data-testid="reflection-text">{l}</p></div>)}</div>
      <div className="surface p-2">
        <p className="px-3 pt-3 pb-2 text-ink/80">Grazie per averlo scritto. Puoi fermarti qui, continuare a raccontare, oppure scegliere cosa fare adesso.</p>
        <button onClick={save} disabled={saved} className="row rounded-2xl" data-testid="reflect-save-btn">{saved ? <Check size={18} className="text-[hsl(var(--primary))]" /> : <BookLock size={18} className="text-[hsl(var(--primary))]" />}<span className="flex-1">{saved ? "Salvato nel diario privato" : savedCount ? "Salva anche le aggiunte" : "Salva nel diario privato"}</span></button>
        {mood && opt(`/oggi/${mood}`, Sparkles, "Qualche passo che può aiutare", "path")}
        {opt("/corpo", Activity, "Ascolta il tuo corpo", "body")}
        {opt("/messaggi", MessageCircle, "Scrivi a una persona di cui ti fidi", "messages")}
        {opt("/diario", NotebookPen, "Apri il diario", "diary")}
      </div>
      <FeelComposer onSend={onMore} compact />
      <button onClick={onClose} className="text-sm text-ink/65 underline min-h-[44px]" data-testid="reflect-new-btn">Ricomincia</button>
    </div>
  );
}

function Shortcut({ to, Icon, title, text, id }: any) {
  return <Link to={to} className="surface p-4 flex items-center gap-4 hover:bg-white/60" data-testid={id}><span className="w-11 h-11 rounded-2xl tone-cyan flex items-center justify-center shrink-0"><Icon size={20} /></span><span className="flex-1 min-w-0"><span className="block font-semibold">{title}</span><span className="block text-sm text-ink/65">{text}</span></span><ArrowRight size={17} className="text-ink/45" /></Link>;
}

export default function Oggi() {
  const { user } = useAuth();
  const [mood, setMood] = useState<string | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  return (
    <div className="px-5 md:px-12 pb-12 max-w-2xl mx-auto" data-testid="oggi-page">
      <section className={`flex flex-col justify-center ${lines.length ? "pt-8" : "min-h-[calc(78dvh-var(--top-h)-var(--nav-h))] py-10"}`}>
        {!lines.length ? (
          <div className="space-y-7">
            <header className="text-center">
              <p className="text-sm text-ink/62 mb-3">Ciao{user?.display_name ? `, ${user.display_name}` : ""}</p>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="oggi-question">Come ti senti oggi?</h1>
              <p className="text-base text-ink/66 mt-3">Puoi partire da dove sei.</p>
            </header>
            <FeelComposer onSend={(t: string) => setLines([t])} mood={mood} setMood={setMood} />
            <p className="text-xs text-ink/55 text-center">Qui non risponde un'intelligenza artificiale: scrivi per te, e decidi tu se salvarlo o parlarne con qualcuno.</p>
          </div>
        ) : <Reflection lines={lines} mood={mood} onMore={(t: string) => setLines((xs) => [...xs, t])} onClose={() => { setLines([]); setMood(null); }} />}
      </section>
      <div className="space-y-4 mt-4">
        <Shortcut to="/corpo" Icon={Activity} title="Ascolta il tuo corpo" text="Tocca dove senti qualcosa e descrivilo" id="oggi-body-link" />
        <Shortcut to="/diario" Icon={NotebookPen} title="Il mio diario" text="Pensieri e sensazioni, visibili solo a te" id="oggi-diary-link" />
        <TodayGoals />
        <QuickSupport />
        <div className="surface p-2 flex items-center gap-2" data-testid="oggi-help-row">
          <Link to="/aiuto" className="row rounded-2xl flex-1" data-testid="oggi-help-link"><LifeBuoy size={18} className="text-[hsl(var(--primary))]" /><span className="flex-1 font-semibold">Aiuto e contatti</span><ArrowRight size={16} className="text-ink/45" /></Link>
          <a href="tel:112" className="btn btn-danger shrink-0 mr-1" data-testid="oggi-call-112"><Phone size={15} />112</a>
        </div>
      </div>
    </div>
  );
}

export function Aiuto() {
  const [c, setC] = useState(getCountry());
  const pick = (code: string) => { setCountry(code); setC(getCountry()); };
  return (
    <div className="px-5 md:px-12 pt-6 md:pt-12 pb-12 max-w-2xl mx-auto space-y-6" data-testid="aiuto-page">
      <BackButton parent="/" />
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Aiuto e contatti</h1>
      <label className="block">
        <span className="text-sm font-medium text-ink/75">Paese in cui ti trovi</span>
        <select className="field mt-1.5" value={c.code} onChange={(e) => pick(e.target.value)} data-testid="help-country-select">
          {COUNTRIES.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
        </select>
      </label>
      <HelpBox country={c} />
      <p className="text-xs text-ink/55 px-1" data-testid="help-numbers-note">Numeri ufficiali dei servizi nazionali. Se un numero non risponde o hai un dubbio, chiama il {c.emergency}.</p>
      <TrustedContacts />
    </div>
  );
}

const PATHS: Record<string, { title: string; text: string; steps: [string, string][]; actions: [string, string, any][] }> = {
  bene: {
    title: "Sto bene", text: "Bello sentirlo. Se ti va, puoi dare una direzione a questa energia.",
    steps: [["Fissa un piccolo obiettivo", "Qualcosa di concreto che puoi fare oggi o questa settimana."], ["Nota cosa ti ha fatto stare bene", "Riconoscerlo aiuta a ritrovarlo nei giorni più difficili."]],
    actions: [["/cresci", "Crea un obiettivo", Sprout], ["/pubblica", "Condividi un piccolo progresso", PenLine], ["/cresciamo", "Incoraggia qualcuno", Users]],
  },
  giu: {
    title: "Mi sento giù", text: "Va bene non stare bene. Non serve sistemare tutto adesso: basta un passo piccolo.",
    steps: [["Un gesto per il corpo", "Un bicchiere d'acqua, un po' di luce, cinque minuti all'aria aperta."], ["Un contatto", "Scrivi a una persona di cui ti fidi, anche solo per dire come stai."], ["Se dura da settimane", "Se questo stato pesa da più di due settimane o rende difficile la giornata, parlane con il medico o con uno psicologo."]],
    actions: [["/messaggi", "Scrivi a qualcuno", MessageCircle], ["/pubblica", "Racconta, se vuoi", PenLine], ["/cresci", "Un obiettivo minuscolo", Sprout]],
  },
  ansia: {
    title: "Sono in ansia", text: "L'ansia è faticosa, ma puoi aiutare il corpo a rallentare. Prova uno di questi esercizi, al tuo ritmo.",
    steps: [["Respiro lento", "Inspira dal naso contando fino a 4, espira dalla bocca contando fino a 6. Ripeti 5 volte: l'espirazione più lunga aiuta a calmarsi."], ["5-4-3-2-1", "Nomina 5 cose che vedi, 4 che tocchi, 3 che senti, 2 odori, 1 sapore. Riporta l'attenzione al presente."], ["Attenzione ai sintomi fisici", "Non dare per scontato che sia ansia: dolore al petto, fatica a respirare, svenimento o sintomi nuovi e intensi richiedono il 112."]],
    actions: [["/messaggi", "Scrivi a qualcuno", MessageCircle], ["/cresciamo", "Non sei l'unica persona", Users]],
  },
  solo: {
    title: "Mi sento solo", text: "Sentirsi soli capita a molte persone, anche quando non si vede. Puoi iniziare da un contatto piccolo.",
    steps: [["Un messaggio semplice", "\"Ciao, pensavo a te\" basta per riaprire un contatto."], ["Una community che ascolta", "In Cresciamo e nel feed trovi persone che condividono percorsi simili. Non sempre qualcuno risponde subito: per un ascolto immediato ci sono le linee qui sotto."]],
    actions: [["/cresciamo", "Vai a Cresciamo", Users], ["/messaggi", "Apri i messaggi", MessageCircle], ["/feed", "Leggi storie vere", PenLine]],
  },
};

export function MoodPath() {
  const { mood = "" } = useParams();
  const p = PATHS[mood];
  if (!p) return <div className="p-10"><Link to="/" className="btn btn-ghost">Torna a Oggi</Link></div>;
  return (
    <div className="px-5 md:px-12 pt-6 md:pt-12 pb-12 max-w-2xl mx-auto space-y-8" data-testid={`mood-path-${mood}`}>
      <BackButton parent="/" />
      <header>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{p.title}</h1>
        <p className="text-ink/70 mt-3 leading-relaxed">{p.text}</p>
      </header>
      <ol className="surface p-2" data-testid="mood-steps">
        {p.steps.map(([t, d], i) => (
          <li key={t} className="row items-start rounded-2xl">
            <span className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-sm font-bold shrink-0">{i + 1}</span>
            <div><div className="font-semibold">{t}</div><p className="text-sm text-ink/70 mt-0.5 leading-relaxed">{d}</p></div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2" data-testid="mood-actions">
        {p.actions.map(([to, l, Icon]) => <Link key={to} to={to} className="btn btn-ghost" data-testid={`mood-action-${to.slice(1)}`}><Icon size={16} />{l}</Link>)}
      </div>
      <HelpBox />
    </div>
  );
}
